/**
 * supernovaPipeline — expanding shockwave from a central point.
 *
 * A radial wave expands outward over time. The wave has a sharp
 * leading edge with a hot core (white/yellow) transitioning to
 * orange/red as it expands, then violet/blue at the trailing edge.
 *
 * Behind the wave: hot ejecta filaments (perlin-driven).
 * At the center: a persistent bright core (the "neutron star" remnant).
 *
 * Audio:
 *   - bass: drives the wave radius and intensity
 *   - mid:  drives the ejecta filaments
 *   - high: drives secondary shockwaves / sparkle bursts
 *
 * No mesh, no simulation — single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface SupernovaPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * valueNoise(p);
    p = p * 2.0 + 3.7;
    a *= 0.5;
  }
  return v;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);
  float theta = atan(p.y, p.x);

  float t = uTime;

  // Primary shockwave: expanding circle that grows with time.
  // We "loop" it so it keeps restarting — every 6 seconds.
  float loopT = mod(t, 6.0);
  float waveRadius = 0.05 + loopT * 0.35 + bass * 0.15;

  // Distance to the wave (negative inside, positive outside).
  float waveDist = abs(r - waveRadius);

  // The wave is a thin band — sharp leading edge.
  float waveThickness = 0.04 + bass * 0.05;
  float wave = smoothstep(waveThickness, 0.0, waveDist);

  // Color shifts along the wave: hot core inside, cooler outside.
  // Inner color: white/yellow.
  // Outer color: orange/red transitioning to violet.
  vec3 waveColorInner = vec3(1.0, 0.95, 0.7);
  vec3 waveColorOuter = vec3(0.95, 0.3, 0.1);
  vec3 waveColorFar = vec3(0.4, 0.1, 0.6);

  vec3 col;
  if (r < waveRadius) {
    // Inside the wave: hot ejecta.
    col = mix(waveColorInner, waveColorOuter, smoothstep(0.0, waveRadius, r));
  } else {
    // Outside: cooler trailing edge.
    col = mix(waveColorOuter, waveColorFar, smoothstep(0.0, 0.3, r - waveRadius));
  }

  // Apply wave intensity.
  col *= wave * (1.5 + high * 0.8);

  // Ejecta filaments: perlin-driven chaotic detail inside the wave.
  float ejecta = fbm(p * 8.0 + vec2(t * 0.5, t * 0.3));
  float ejectaMask = smoothstep(waveRadius + 0.05, 0.0, r);
  col += vec3(1.0, 0.5, 0.2) * ejecta * ejectaMask * mid * 0.6;

  // Persistent central core (neutron star remnant).
  float core = smoothstep(0.05, 0.0, r);
  vec3 coreColor = vec3(1.0, 1.0, 0.95);
  col += coreColor * core * (0.8 + bass * 0.5);

  // Secondary shockwaves — rings spreading out behind the main one.
  for (int i = 1; i <= 3; i++) {
    float fi = float(i);
    float secondaryR = waveRadius - fi * 0.08 - 0.05;
    if (secondaryR > 0.0) {
      float secondaryDist = abs(r - secondaryR);
      float secondaryWave = smoothstep(0.02, 0.0, secondaryDist);
      float secondaryFade = 1.0 - fi * 0.25;
      col += vec3(1.0, 0.7, 0.4) * secondaryWave * secondaryFade * high * 0.3;
    }
  }

  // Sparkle bursts at the wave front (high audio accent).
  float sparkAngle = valueNoise(vec2(floor(theta * 8.0), floor(t * 4.0)));
  float sparkMask = smoothstep(waveRadius - 0.05, waveRadius + 0.05, r) *
                    smoothstep(waveRadius + 0.05, waveRadius - 0.05, r);
  float spark = step(0.85, sparkAngle) * sparkMask * high * 2.0;
  col += vec3(1.0, 0.9, 0.6) * spark;

  // Background — deep space with very faint nebula tint.
  vec3 bg = vec3(0.01, 0.01, 0.04);
  bg += vec3(0.05, 0.02, 0.08) * exp(-r * 2.0) * 0.5;
  // Blend bg behind the wave.
  col = mix(bg, col, clamp(length(col), 0.0, 1.0));

  // Tone mapping (ACES-approximation).
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeSupernovaBands(t: number, out: Float32Array): void {
  // Strong bass on every 6-second loop, with secondary pulses.
  const loopT = t % 6.0;
  const impact = Math.exp(-loopT * 1.5);  // sharp attack then decay
  const secondaryPhase = ((loopT + 2) % 6) - 2;
  const secondary = Math.exp(-(secondaryPhase * secondaryPhase) / 2.0) * 0.4;
  out[0] = 0.3 + 0.5 * impact + 0.3 * Math.sin(t * 0.6);
  out[1] = 0.4 + 0.4 * impact + 0.2 * Math.sin(t * 0.9 + 1.0);
  out[2] = 0.3 + 0.4 * impact + 0.3 * Math.sin(t * 1.2 + 2.0);
  out[3] = 0.4 + 0.3 * secondary + 0.3 * Math.sin(t * 1.4 + 1.5);
  out[4] = 0.4 + 0.3 * secondary + 0.3 * Math.sin(t * 1.7 + 0.5);
  out[5] = 0.3 + 0.3 * secondary + 0.3 * Math.sin(t * 2.0 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.5 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 3.0 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.5 + 3.0);
}

export function computeSupernovaRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030],
    [0.030, 0.080],
    [0.080, 0.150],
    [0.150, 0.260],
    [0.260, 0.380],
    [0.380, 0.500],
    [0.500, 0.660],
    [0.660, 0.820],
    [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createSupernovaPipeline(gl: WebGL2RenderingContext): SupernovaPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawSupernova(
  gl: WebGL2RenderingContext,
  pipe: SupernovaPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
